import { DefaultOptionType } from 'antd/lib/select';
import { useEffect, useRef, useState } from 'react';
import { Chip, Ms, MsBox, MsCaret, MsEmpty, MsList, MsOpt, MsPh, MsPop, MsSearch } from '../ProgramSetup.styled';

interface Props {
  value?: string[];
  options?: DefaultOptionType[];
  onChange: (value: string[]) => void;
  onSearch: (keyword: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export const AnchorMultiSelect = ({ value, options, onChange, onSearch, loading, disabled, placeholder }: Props) => {
  const selected = value || [];
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;

    const onClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const labelFor = (id: string) => options?.find((option) => option.value === id)?.label ?? id;

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((item) => item !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const remove = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(selected.filter((item) => item !== id));
  };

  return (
    <Ms ref={rootRef}>
      <MsBox
        $open={open}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen(true)}
      >
        {selected.map((id) => (
          <Chip key={id}>
            <span>{labelFor(id)}</span>
            <button type="button" aria-label={`Remove ${labelFor(id)}`} onClick={(event) => remove(id, event)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </Chip>
        ))}
        {selected.length === 0 && <MsPh>{placeholder || 'Select anchors or reporters…'}</MsPh>}
        {!disabled && (
          <MsCaret type="button" tabIndex={-1} aria-label="Toggle list" onClick={() => setOpen((current) => !current)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </MsCaret>
        )}
        {open && (
          <MsPop onClick={(event) => event.stopPropagation()}>
            <MsSearch>
              <input
                className="ant-input"
                type="text"
                placeholder="Search anchors and reporters…"
                autoComplete="off"
                autoFocus
                onChange={(event) => onSearch(event.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  border: '1px solid var(--line)',
                  borderRadius: 9,
                  padding: '0 12px',
                }}
              />
            </MsSearch>
            <MsList role="listbox" aria-multiselectable="true">
              {loading && <MsEmpty>Searching…</MsEmpty>}
              {!loading && (options?.length ?? 0) === 0 && <MsEmpty>No matches</MsEmpty>}
              {!loading &&
                options?.map((option) => (
                  <MsOpt
                    key={option.value as string}
                    type="button"
                    role="option"
                    aria-selected={selected.includes(option.value as string)}
                    $selected={selected.includes(option.value as string)}
                    onClick={() => toggle(option.value as string)}
                  >
                    <span className="bx">
                      {selected.includes(option.value as string) && (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                        >
                          <path d="m5 12 5 5L20 7" />
                        </svg>
                      )}
                    </span>
                    {option.label}
                  </MsOpt>
                ))}
            </MsList>
          </MsPop>
        )}
      </MsBox>
    </Ms>
  );
};
